import React from 'react';
import {
  Grid,
  Typography,
  CssBaseline,
  Container,
  Box,
  Card,
  Divider
} from '@mui/material';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SEO from '../../components/SEO';
import { Navbar } from '../Navbar/Navbar';
import { Footer } from '../Footer';
import { Colors } from '../style/theme';

const TopMargin = styled.div`
  margin-top: 100px;
`;

const StyledCard = styled.div`
  background-color: #f7f7f7;
  padding: 0px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
`;

const caseStudyData = [
  {
    title:
      'Digital Petroleum Retail Startup Achieves 24/7 Infrastructure Visibility',
    description:
      'A petroleum eCommerce startup transformed its infrastructure monitoring with WatchNod — moving from manual checks and blind spots to real-time dashboards, alerts, and zero downtime.',
    author: 'WatchNod Team',
    date: 'August 1, 2025',
    id: 'caseStudy-1'
  },
  {
    title:
      'E-Commerce Platform Survives Flash Sales Without Downtime',
    description:
      'A mid-sized eCommerce brand avoided server crashes during high-traffic flash sales by implementing real-time monitoring, instant alerts, and load trend analysis with WatchNod.',
    author: 'WatchNod Team',
    date: 'August 1, 2025',
    id: 'caseStudy-2'
  },
  {
    title:
      'SaaS Startup Gains Early Stability with Scalable Monitoring',
    description:
      'A fast-growing SaaS startup improved stability by deploying scalable Kubernetes monitoring, real-time alerts, and long-term performance analytics with WatchNod.',
    author: 'WatchNod Team',
    date: 'August 1, 2025',
    id: 'caseStudy-3'
  },
  {
    title:
      'Freelancer DevOps Engineer Offers Premium Monitoring as a Service',
    description:
      'A freelance DevOps consultant centralized multi-client monitoring, improved uptime reporting, and turned WatchNod into a monetizable service offering.',
    author: 'WatchNod Team',
    date: 'August 1, 2025',
    id: 'caseStudy-4'
  }
];

const titleToSlug = (title) => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

function CaseStudyPage() {
  const leftItems = caseStudyData.filter((_, i) => i % 2 === 0);
  const rightItems = caseStudyData.filter((_, i) => i % 2 !== 0);

  return (
    <>
      <SEO
        title="WatchNod Case Studies | Monitoring Success Stories"
        description="Explore WatchNod case studies to see how businesses prevent downtime, improve uptime, and manage server, API, and domain monitoring effectively."
        url="/case-studies"
        type="website"
      />
      <Navbar />
      <TopMargin />
      <CssBaseline />
      <StyledCard>
        <Card>
          <Container maxWidth="xl" sx={{ py: 6 }}>
            <Typography
              variant="h3"
              gutterBottom
              textAlign="center"
              sx={{ fontWeight: 700 }}
            >
              From Freelancers to Enterprises: Infrastructure Wins with WatchNod
            </Typography>

            <Grid container spacing={0} alignItems="stretch" my={6}>
              {/* Left column */}
              <Grid item xs={12} md={5}>
                {leftItems.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 6,
                      minHeight: "200px",
                      pr: { md: 4 },
                      pb: 4,
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mt: 5,
                        mb: 1,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography color="text.secondary">
                      {item.description}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                          color="text.primary"
                        >
                          {item.author}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.date}
                        </Typography>
                      </Box>

                      <Typography
                        variant="button"
                        component={Link}
                        to={`/case-studies/${titleToSlug(item.title)}`}
                        sx={{
                          textDecoration: 'none',
                          color: Colors.primary,
                          fontWeight: 700,
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        Read
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Grid>

              {/* Divider */}
              <Grid
                item
                md={2}
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'center'
                }}
              >
                <Divider
                  orientation="vertical"
                  sx={{
                    height: '100%',
                    borderColor: '#d1d5db',
                    borderRightWidth: 2
                  }}
                />
              </Grid>

              {/* Right column */}
              <Grid item xs={12} md={5}>
                {rightItems.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 6,
                      minHeight: "200px",
                      pr: { md: 4 },
                      pb: 4,
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mt: 5,
                        mb: 1,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography color="text.secondary">
                      {item.description}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                          color="text.primary"
                        >
                          {item.author}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.date}
                        </Typography>
                      </Box>

                      <Typography
                        variant="button"
                        component={Link}
                        to={`/case-studies/${titleToSlug(item.title)}`}
                        sx={{
                          textDecoration: 'none',
                          color: Colors.primary,
                          fontWeight: 700,
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        Read
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </Container>
        </Card>
      </StyledCard>
      <Footer />
    </>
  );
}

export default CaseStudyPage;
